import React, { useState, useEffect, useRef } from "react";

import {
  useEditor,
  EditorContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from "@tiptap/react";

import Link from "@tiptap/extension-link";

import { Node, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";

import {
  Paper,
  Stack,
  IconButton,
  Divider,
  Tooltip,
  Box,
  TextField,
} from "@mui/material";

import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ImageIcon from "@mui/icons-material/Image";
import TitleIcon from "@mui/icons-material/Title";

import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { LinkIcon } from "lucide-react";

export default function BlogBodyEditor({ value, onChange }) {
  const fileInputRef = useRef(null);

  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // wichtig: nichts mit image hier
      }),
      ImageWithCaption.configure({
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;

    const current = editor.getHTML();
    if (current !== value) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const reader = new FileReader();

    reader.onload = () => {
      editor
        ?.chain()
        .focus()
        .insertContent({
          type: "imageWithCaption",
          attrs: {
            src: reader.result,
            width: "medium",
            align: "center",
            caption: "",
          },
        })
        .run();
    };

    reader.readAsDataURL(file);

    event.target.value = "";
  };

  const applyLink = () => {
    if (!linkUrl) {
      editor.chain().focus().unsetLink().run();
      setLinkOpen(false);
      return;
    }

    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: linkUrl })
      .run();

    setLinkOpen(false);
  };

  if (!editor) {
    return null;
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          p: 1,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Tooltip title="Fett">
          <IconButton
            size="small"
            color={editor.isActive("bold") ? "primary" : "default"}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <FormatBoldIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Kursiv">
          <IconButton
            size="small"
            color={editor.isActive("italic") ? "primary" : "default"}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <FormatItalicIcon />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        <Tooltip title="Überschrift H2">
          <IconButton
            size="small"
            color={
              editor.isActive("heading", { level: 2 }) ? "primary" : "default"
            }
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
          >
            <TitleIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Liste">
          <IconButton
            size="small"
            color={editor.isActive("bulletList") ? "primary" : "default"}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <FormatListBulletedIcon />
          </IconButton>
        </Tooltip>

        <IconButton
          size="small"
          onClick={() => {
            const previousUrl = editor.getAttributes("link").href;
            setLinkUrl(previousUrl || "");
            setLinkOpen(true);
          }}
        >
          <LinkIcon />
        </IconButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* IMAGE */}
        <Tooltip title="Bild einfügen">
          <IconButton size="small" onClick={() => fileInputRef.current.click()}>
            <ImageIcon />
          </IconButton>
        </Tooltip>

        <input
          hidden
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
        />
      </Stack>

      <Divider />

      <Box
        sx={{
          p: 2,
          minHeight: 300,

          "& .ProseMirror": {
            outline: "none",
            minHeight: 250,
          },

          "& .ProseMirror h1": {
            typography: "h4",
            mb: 2,
          },

          "& .ProseMirror h2": {
            typography: "h5",
            mb: 2,
            mt: 3,
          },

          "& .ProseMirror h3": {
            typography: "h6",
            mb: 1,
            mt: 2,
          },

          "& .ProseMirror p": {
            mb: 1.5,
          },

          "& .ProseMirror ul": {
            pl: 3,
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      <Dialog open={linkOpen} onClose={() => setLinkOpen(false)} fullWidth>
        <DialogTitle>Link einfügen</DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            label="URL"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://..."
            autoFocus
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setLinkOpen(false)}>Abbrechen</Button>
          <Button onClick={applyLink} variant="contained">
            Übernehmen
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export const ImageWithCaption = Node.create({
  name: "imageWithCaption",

  group: "block",
  atom: true,

  addAttributes() {
    return {
      src: {},
      alt: { default: "" },
      width: { default: "large" },
      align: { default: "center" },
      caption: { default: "" },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'figure[data-type="image"]',
        getAttrs: (el) => {
          const img = el.querySelector("img");
          const caption = el.querySelector("figcaption");

          if (!img) return false;

          return {
            src: img.getAttribute("src"),
            width: img.getAttribute("data-width") || "large",
            align: img.getAttribute("data-align") || "center",
            caption: caption?.textContent || "",
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "figure",
      { "data-type": "image" },

      [
        "img",
        mergeAttributes({
          src: HTMLAttributes.src,
          "data-width": HTMLAttributes.width,
          "data-align": HTMLAttributes.align,
        }),
      ],

      ["figcaption", { contenteditable: "true" }, HTMLAttributes.caption || ""],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageWithCaptionView);
  },
});

export function ImageWithCaptionView({ node, updateAttributes, selected }) {
  const [hover, setHover] = useState(false);

  const setCaption = (e) => {
    updateAttributes({ caption: e.target.value });
  };

  const getWidth = (w) => {
    switch (w) {
      case "small":
        return "200px";
      case "medium":
        return "400px";
      default:
        return "100%";
    }
  };

  return (
    <NodeViewWrapper
      style={{ position: "relative", display: "block" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <img
        src={node.attrs.src}
        style={{
          width: getWidth(node.attrs.width),
          display: "block",
          margin:
            node.attrs.align === "center"
              ? "0 auto"
              : node.attrs.align === "right"
                ? "0 0 0 auto"
                : "0",
        }}
      />

      {/* Caption Input */}
      <TextField
        value={node.attrs.caption || ""}
        onChange={setCaption}
        variant="standard"
        fullWidth
        placeholder="Bildbeschreibung..."
        sx={{
          mt: 1,
          fontSize: "0.9rem",
        }}
      />

      {/* Toolbar */}
      {(hover || selected) && (
        <Paper
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            p: 0.5,
          }}
        >
          <Stack direction="row" spacing={0.5}>
            {/* ALIGN */}
            <Tooltip title="Left">
              <IconButton
                size="small"
                onClick={() => updateAttributes({ align: "left" })}
              >
                <FormatAlignLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Center">
              <IconButton
                size="small"
                onClick={() => updateAttributes({ align: "center" })}
              >
                <FormatAlignCenterIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Right">
              <IconButton
                size="small"
                onClick={() => updateAttributes({ align: "right" })}
              >
                <FormatAlignRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* SIZE */}
            <button onClick={() => updateAttributes({ width: "small" })}>
              S
            </button>
            <button onClick={() => updateAttributes({ width: "medium" })}>
              M
            </button>
            <button onClick={() => updateAttributes({ width: "large" })}>
              L
            </button>
          </Stack>
        </Paper>
      )}
    </NodeViewWrapper>
  );
}
